import { ReactNode, useCallback, useEffect, useRef } from 'react'
import {
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  Modifier,
  pointerWithin,
  useDndContext
} from '@dnd-kit/core'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { editorSelectors } from 'store/features/editor/selectors'
import { editorThunks } from 'store/features/editor/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataMediaThunks } from 'store/features/projectData/thunks/media'
import { recentlyUsedMediaThunks } from 'store/features/recentlyUsedMedia/thunks'
import { initialDropState, useTimelineContext } from 'context/TimelineContext'
import { TIMELINE_CONTENT_WRAPPER_ID } from 'containers/Timeline/MainContainer/consts'
import { IBaseMediaItem, MediaStatus } from 'types/media'
import { secondsToPixels, computePreviewMediaItems } from 'utils/timeline'
import { convertBaseItemToMediaItem } from 'utils/media'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { useTimelineDropPosition } from 'hooks/useTimelineDropPosition'
import DraggingItemPlaceholder from 'components/DraggingItemPlaceholder'
import { isValueUndefined } from 'utils'
import { useDndSensors } from 'hooks/useDndSensors'
import { getDropLineKey } from 'utils/timelineZIndex'

// Keeps the overlay (placeholder) centered on the cursor.
// Uses the overlay's own rect so ghost width can differ from source width.
const snapToCenterModifier: Modifier = ({
  activatorEvent,
  draggingNodeRect,
  overlayNodeRect,
  transform
}) => {
  if (!activatorEvent || !draggingNodeRect || !overlayNodeRect) return transform

  const { clientX, clientY } = activatorEvent as PointerEvent

  return {
    ...transform,
    x:
      transform.x +
      (clientX - draggingNodeRect.left) -
      overlayNodeRect.width / 2,
    y:
      transform.y +
      (clientY - draggingNodeRect.top) -
      overlayNodeRect.height / 2
  }
}

const DragOverlayContent = () => {
  const { active } = useDndContext()
  const item = active?.data.current?.item as IBaseMediaItem | undefined

  return (
    <DragOverlay
      dropAnimation={null}
      modifiers={[snapToCenterModifier]}
      style={{ pointerEvents: 'none' }}
    >
      {item ? <DraggingItemPlaceholder item={item} /> : null}
    </DragOverlay>
  )
}

// While a dnd-kit drag is active, force the cursor to `grabbing` everywhere.
// The overlay has pointer-events: none, so the cursor comes from whatever is
// under the pointer — setting it on <html> overrides that globally.
const setBodyCursorGrabbing = () => {
  document.documentElement.style.cursor = 'grabbing'
  document.body.classList.add('is-dnd-dragging')
}
const restoreBodyCursor = () => {
  document.documentElement.style.cursor = ''
  document.body.classList.remove('is-dnd-dragging')
}

const EditorDndContext = ({ children }: { children: ReactNode }) => {
  const { setPreviewMediaItems, setDropState } = useTimelineContext()

  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const existingMediaItems = useSelector(projectDataSelectors.mediaItems)
  const defaultDimensions = useSelector(projectDataSelectors.defaultDimensions)
  const { isDurationAllowed } = useDurationLimitCheck()
  const { getDropPositionFromDndKit } = useTimelineDropPosition()

  const existingMediaItemsRef = useRef(existingMediaItems)
  existingMediaItemsRef.current = existingMediaItems

  // Live viewport pointer position, updated from a document-level pointermove
  // listener attached for the duration of each drag. Using this instead of
  // `activatorEvent + delta` avoids dnd-kit's delta drifting from the cursor
  // when the source draggable lives in a scrolled ancestor (e.g. sidebar list).
  const cursorPositionRef = useRef<{ x: number; y: number } | null>(null)

  // Used to skip redundant setState calls on every pixel-move when the
  // insertion slot hasn't changed. Without this guard, committing a new
  // previewMediaItems array on each move triggers a re-render cascade inside
  // the nested MultiLineDnd DndContext and dnd-kit loops on measurement.
  // Keying on `insertTime|targetZIndex|targetGapAfterLineIndex` so that
  // vertical movement (row change at the same X) still re-runs the update —
  // tracking only insertTime would miss row transitions and gap↔line flips.
  const lastDropKeyRef = useRef<string | null>(null)

  const { sensors } = useDndSensors()

  const clearTimelinePreview = useCallback(() => {
    lastDropKeyRef.current = null
    setPreviewMediaItems(null)
    setDropState(initialDropState)
  }, [setPreviewMediaItems, setDropState])

  //dnd kits internal activator clinetx and y are calculated based on the item position,
  // thus if i drag an item from scrolled sidebar container, positiones are messed
  // that's why we use eventListener
  const handlePointerMove = useCallback((event: PointerEvent) => {
    cursorPositionRef.current = { x: event.clientX, y: event.clientY }
  }, [])

  const stopTrackingPointer = useCallback(() => {
    document.removeEventListener('pointermove', handlePointerMove, {
      capture: true
    } as EventListenerOptions)
    cursorPositionRef.current = null
  }, [handlePointerMove])

  const resetInternalDragState = useCallback(() => {
    clearTimelinePreview()
    stopTrackingPointer()
    restoreBodyCursor()
  }, [clearTimelinePreview, stopTrackingPointer])

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setBodyCursorGrabbing()

      const activator = event.activatorEvent as PointerEvent | null
      cursorPositionRef.current = activator
        ? { x: activator.clientX, y: activator.clientY }
        : null

      document.addEventListener('pointermove', handlePointerMove, {
        capture: true
      })
    },
    [handlePointerMove]
  )

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const { over, active } = event
      if (over?.id !== TIMELINE_CONTENT_WRAPPER_ID) {
        clearTimelinePreview()
        return
      }

      const item = active.data.current?.item as IBaseMediaItem | undefined
      if (!item?.duration) return

      const cursor = cursorPositionRef.current
      if (!cursor) return

      const { insertTime, targetZIndex, targetGapAfterLineIndex } =
        getDropPositionFromDndKit(cursor, item.duration)
      if (isValueUndefined(insertTime) || isValueUndefined(targetZIndex)) return

      const isGapDrop = !isValueUndefined(targetGapAfterLineIndex)

      const dropKey = getDropLineKey({
        isGapDrop,
        insertTime,
        targetGapAfterLineIndex,
        targetZIndex
      })

      if (lastDropKeyRef.current === dropKey) return
      lastDropKeyRef.current = dropKey

      setDropState({
        isDragOver: true,
        rowZIndex: targetZIndex,
        // For gap drops the precise X / row position would only mislead — the
        // BetweenLinesGap highlight is the visual feedback. Suppress the
        // DropIndicator by leaving position/width null.
        positionPx: isGapDrop
          ? null
          : secondsToPixels(insertTime, timelineRulerSliceDuration),
        widthPx: isGapDrop
          ? null
          : secondsToPixels(item.duration, timelineRulerSliceDuration),
        targetGapAfterLineIndex: targetGapAfterLineIndex ?? null
      })
      // No cascade preview for gap drops — the new row is empty; existing
      // items shouldn't shift on the screen.
      if (isGapDrop) {
        setPreviewMediaItems(null)
      } else {
        setPreviewMediaItems(
          computePreviewMediaItems(
            existingMediaItemsRef.current,
            insertTime,
            item.duration,
            targetZIndex
          )
        )
      }
    },
    [
      timelineRulerSliceDuration,
      setPreviewMediaItems,
      setDropState,
      clearTimelinePreview,
      getDropPositionFromDndKit
    ]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const item = event.active.data.current?.item as IBaseMediaItem | undefined
      const cursor = cursorPositionRef.current

      const {
        insertTime,
        targetZIndex,
        previousItemId,
        targetGapAfterLineIndex
      } = cursor
        ? getDropPositionFromDndKit(cursor, item?.duration ?? 0)
        : ({} as ReturnType<typeof getDropPositionFromDndKit>)

      resetInternalDragState()

      if (event.over?.id !== TIMELINE_CONTENT_WRAPPER_ID) return
      if (!item || !isDurationAllowed(item.duration)) return
      if (isValueUndefined(insertTime)) return

      const preparedItem = convertBaseItemToMediaItem(
        item,
        insertTime,
        defaultDimensions,
        targetZIndex
      )

      if (preparedItem.startTime < 0) return

      dispatch(
        projectDataMediaThunks.adjustTimelineForNewItems(
          [preparedItem],
          previousItemId,
          targetZIndex,
          targetGapAfterLineIndex
        )
      )
      dispatch(
        editorThunks.setActiveTimelineItems([
          { id: preparedItem.id, type: preparedItem.type }
        ])
      )

      if (preparedItem.meta.status === MediaStatus.OK) {
        dispatch(
          recentlyUsedMediaThunks.updateRecentlyUsedItemsOnInsertFromMediaLibrary(
            [preparedItem]
          )
        )
      }
    },
    [
      resetInternalDragState,
      getDropPositionFromDndKit,
      isDurationAllowed,
      defaultDimensions
    ]
  )

  const handleDragCancel = useCallback(
    (_event: DragCancelEvent) => {
      resetInternalDragState()
    },
    [resetInternalDragState]
  )

  // Safety net: if the component unmounts mid-drag, the cursor would otherwise
  // stay stuck on `grabbing`.
  useEffect(() => {
    return restoreBodyCursor
  }, [])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {children}
      <DragOverlayContent />
    </DndContext>
  )
}

export default EditorDndContext
